import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../../contexts/AuthContext';
import '../styles/EmailListScreen.css';
import { useLocation } from 'react-router-dom';

// Helper function to export data as CSV
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Define CSV headers
  const headers = ['Email', 'Athlete Name', 'Sport', 'Location', 'Status', 'Submitted Date'];

  // Convert data to CSV rows
  const csvRows = data.map(row => [
    row.email || '',
    row.athlete || '',
    row.sport || '',
    row.location || '',
    row.status || '',
    row.submitted || ''
  ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','));

  // Combine headers and rows
  const csvContent = [headers.join(','), ...csvRows].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const EmailListScreen = ({ setActiveScreen }) => {
  const { getNominations, getEmailSubscribers, loading } = useAuth();
  const [activeItem, setActiveItem] = useState('email-list');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [emailSubscribers, setEmailSubscribers] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [optedInCount, setOptedInCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [optInFilter, setOptInFilter] = useState('All');
  const [sportFilter, setSportFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const location=useLocation();
  useEffect(() => {
    if (setActiveItem) {
      if (location.pathname.includes('/dashboard/nominations')) {
        setActiveItem('nominations');
      } else if (location.pathname.includes('/dashboard/featured')) {
        setActiveItem('athletes');
      } else if (location.pathname.includes('/dashboard/analytics')) {
        setActiveItem('analytics');
      } else if (location.pathname.includes('/dashboard/email-list')) {
        setActiveItem('email-list');
      }
    }
  }, [location.pathname, setActiveItem]);
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // Fetch nominations (which have submitted_at and email info)
    const nomResult = await getNominations();
    if (nomResult.success) {
      const nominationsData = nomResult.data || [];
      setNominations(nominationsData);

      // Calculate counts from nominations (notify_submitter field is on nominations table)
      const optedIn = nominationsData.filter(n => n.notify_submitter === true).length;
      setOptedInCount(optedIn);
      setTotalCount(nominationsData.length);
    }

    // Fetch email subscribers (for reference)
    const emailResult = await getEmailSubscribers?.();
    if (emailResult?.success) {
      setEmailSubscribers(emailResult.data || []);
    }
  };

  const summaryCards = [
    {
      id: 1,
      title: 'Opted-In Emails',
      value: optedInCount.toLocaleString(),
      description: 'Available for export and marketing',
      icon: '/assets/images/check-circle-icon.png',
    },
    {
      id: 2,
      title: 'Not Opted-In',
      value: (totalCount - optedInCount).toLocaleString(),
      description: 'Stored but excluded from exports',
      icon: '/assets/images/x-circle-icon.png',
    },
    {
      id: 3,
      title: 'Total Submissions',
      value: totalCount.toLocaleString(),
      description: 'All time',
      icon: '/assets/images/mail-icon.png',
    },
  ];

  // Combine nominations and email subscribers for display
  const emailListData = nominations.map((nom, index) => ({
    id: nom.id || index,
    email: nom.submitter_email,
    athlete: `${nom.first_name} ${nom.last_name}`,
    sport: nom.primary_sport,
    location: `${nom.city}, ${nom.state}`,
    status: nom.status ? nom.status.charAt(0).toUpperCase() + nom.status.slice(1) : 'Pending',
    submitted: new Date(nom.submitted_at).toLocaleDateString(),
    optedIn: nom.notify_submitter === true,
  }));

  // Get unique sports for dropdown
  const uniqueSports = [...new Set(emailListData.map(item => item.sport).filter(Boolean))];

  // Get unique statuses for dropdown
  const uniqueStatuses = [...new Set(emailListData.map(item => item.status).filter(Boolean))];

  // Filter the data based on filter states
  const filteredData = emailListData.filter(row => {
    // Search filter - check athlete name, email, and location
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = searchQuery === '' ||
      row.athlete?.toLowerCase().includes(searchLower) ||
      row.email?.toLowerCase().includes(searchLower) ||
      row.location?.toLowerCase().includes(searchLower);

    // Opt-in filter
    const matchesOptIn = optInFilter === 'All' ||
      (optInFilter === 'Opted-In' && row.optedIn === true) ||
      (optInFilter === 'Not Opted-In' && row.optedIn === false);

    // Sport filter
    const matchesSport = sportFilter === 'All' || row.sport === sportFilter;

    // Status filter
    const matchesStatus = statusFilter === 'All' || row.status === statusFilter;

    return matchesSearch && matchesOptIn && matchesSport && matchesStatus;
  });

  // Handle export opted-in emails
  const handleExportOptedIn = () => {
    const optedInData = emailListData.filter(row => row.optedIn);
    if (optedInData.length === 0) {
      alert('No opted-in emails to export');
      return;
    }
    const filename = `opted-in-emails-${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(optedInData, filename);
  };

  return (
    <div className="email-list-screen">
      <DashboardSidebar
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        setActiveScreen={setActiveScreen}
      />

      <div className="email-list-content">
        <DashboardHeader
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          title="Email List Manager"
          subtitle="View, filter, and export email opt-in lists"
        />

        <Container fluid className="email-list-container">
          {/* Summary Cards */}
          <div className="email-summary-grid">
            {summaryCards.map((card) => (
              <div key={card.id} className="email-summary-card">
                <div className="email-summary-header">
                  <span className="email-summary-title">{card.title}</span>
                  <img src={card.icon} alt="" className="email-summary-icon" />
                </div>
                <div className="email-summary-value">{card.value}</div>
                <div className="email-summary-description">{card.description}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="email-filter-row">
            <div className="email-filter-item">
              <label className="email-filter-label">Search</label>
              <div className="email-search-wrapper">
                <input
                  type="text"
                  className="email-search-input"
                  placeholder="Search by the Athlete name, email or location"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="email-filter-item">
              <label className="email-filter-label">Opt-In Status</label>
              <div className="email-select-wrapper">
                <select
                  className="email-filter-select"
                  value={optInFilter}
                  onChange={(e) => setOptInFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="Opted-In">Opted-In</option>
                  <option value="Not Opted-In">Not Opted-In</option>
                </select>
                <img src="/assets/images/arrow-down-icon.png" alt="" className="email-select-arrow" />
              </div>
            </div>

            <div className="email-filter-item">
              <label className="email-filter-label">Sport</label>
              <div className="email-select-wrapper">
                <select
                  className="email-filter-select"
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  {uniqueSports.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
                <img src="/assets/images/arrow-down-icon.png" alt="" className="email-select-arrow" />
              </div>
            </div>

            <div className="email-filter-item">
              <label className="email-filter-label">Status</label>
              <div className="email-select-wrapper">
                <select
                  className="email-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <img src="/assets/images/arrow-down-icon.png" alt="" className="email-select-arrow" />
              </div>
            </div>
          </div>

          {/* Table Header */}
          <div className="email-table-info">
            <span>
              Showing {filteredData.length} of {totalCount} Submissions{' '}
              <strong>({filteredData.filter(r => r.optedIn).length} eligible for export)</strong>
            </span>

            <button className="email-export-button" onClick={handleExportOptedIn}>
              <img src="/assets/images/export-icon.png" alt="" className="email-export-icon" />
              Export Opted-In ({optedInCount})
            </button>
          </div>

          {/* Email Table */}
          <div className="email-table-wrapper">
            <table className="email-table">
              <thead>
                <tr>
                  <th>OPT-IN</th>
                  <th>Email</th>
                  <th>Athlete</th>
                  <th>Sports</th>
                  <th>Locations</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <img
                          src={row.optedIn ? "/assets/images/checked-icon.png" : "/assets/images/x-circle-icon.png"}
                          alt={row.optedIn ? "Opted In" : "Not Opted In"}
                          className="email-optin-icon"
                        />
                      </td>
                      <td>{row.email}</td>
                      <td>{row.athlete}</td>
                      <td>{row.sport}</td>
                      <td>{row.location}</td>
                      <td>
                        <span
                          className={`email-row-status email-row-status-${row.status.toLowerCase()}`}
                        >
                          <span className="email-status-dot"></span>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.submitted}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                      No results found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Compliance Rules */}
          <div className="email-rules-box">
            <h4>Email Compliance Rules</h4>
            <ul>
              <li>Emails are captured only on successful nomination submission</li>
              <li>Only opt-in=true emails are eligible for export and marketing</li>
              <li>Non-opt-in emails are stored for reference but excluded from all exports</li>
              <li>
                Export includes only opted-in emails with full athlete and submission metadata
              </li>
            </ul>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default EmailListScreen;
